/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Trigger syncs with Dagster", description = "Learn how to trigger syncs and gain visibility into sync execution trends with Hightouch's Dagster extension.", lastUpdatedDate = new Date(1675814400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Trigger syncs with Dagster",
        description: "Learn how to trigger syncs and gain visibility into sync execution trends with Hightouch's Dagster extension.",
        lastUpdatedDate: "Wed Feb 08 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Create an API key",
          "depth": 1,
          "id": "create-an-api-key"
        }, {
          "value": "Install library",
          "depth": 1,
          "id": "install-library"
        }, {
          "value": "Configure library",
          "depth": 1,
          "id": "configure-library"
        }, {
          "value": "Set sync schedule type",
          "depth": 1,
          "id": "set-sync-schedule-type"
        }, {
          "value": "Schedule syncs",
          "depth": 1,
          "id": "schedule-syncs"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      h3: "h3",
      code: "code",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {CreateAPIKeySnippet, APIKeyWarning, SetSyncSchedule} = _components;
    if (!APIKeyWarning) _missingMdxReference("APIKeyWarning", true);
    if (!CreateAPIKeySnippet) _missingMdxReference("CreateAPIKeySnippet", true);
    if (!SetSyncSchedule) _missingMdxReference("SetSyncSchedule", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch provides a Dagster extension to trigger syncs and gain visibility into sync execution trends. The ", _jsx(_components.a, {
          href: "https://github.com/hightouchio/dagster-hightouch",
          children: "Git repository"
        }), " contains the latest code."]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.h3, {
        id: "create-an-api-key",
        children: "Create an API key"
      }), "\n", _jsx(_components.p, {
        children: "The first step is to create a Hightouch API key in your Hightouch workspace settings."
      }), "\n", _jsx(CreateAPIKeySnippet, {}), "\n", _jsx(APIKeyWarning, {}), "\n", _jsx(_components.h3, {
        id: "install-library",
        children: "Install library"
      }), "\n", _jsxs(_components.p, {
        children: ["The simplest way to install the Hightouch Dagster extension is by using ", _jsx(_components.code, {
          children: "pip"
        }), "."]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "pip install dagster-hightouch\n"
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-library",
        children: "Configure library"
      }), "\n", _jsxs(_components.p, {
        children: ["To configure the library, provide your Hightouch API key as a resource in the ", _jsx(_components.code, {
          children: "resources.py"
        }), " file:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-python",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# resources.py"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " dagster_hightouch.resources ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " ht_resource ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " hightouch_resource\n\nht_resource = hightouch_resource.configured(\n    {", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"api_key\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"555555-4444-3333-2222-1111111111\""
          }), "},\n)\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-sync-schedule-type",
        children: "Set sync schedule type"
      }), "\n", _jsx(SetSyncSchedule, {
        extension: "Dagster"
      }), "\n", _jsx(_components.h3, {
        id: "schedule-syncs",
        children: "Schedule syncs"
      }), "\n", _jsxs(_components.p, {
        children: ["You can schedule syncs by using the ", _jsx(_components.code, {
          children: "hightouch_sync_op"
        }), " function with the sync IDs of the syncs you want to schedule."]
      }), "\n", _jsx(_components.p, {
        children: "This example creates a job to trigger two syncs—one for Salesforce account objects and one for organization objects—every thirty minutes."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-python",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# The hightouch_sync_op calls the Hightouch API to trigger a sync, and then polls the API until the sync either completes or returns an error."
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " dagster ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " ScheduleDefinition, get_dagster_logger, job\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " dagster_hightouch.ops ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " hightouch_sync_op\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " .resources ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " ht_resource\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Set sync IDs as constants"
          }), "\n\nSFDC_ACCOUNTS_SYNC_ID = ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"23620\""
          }), "\nSFDC_ORGS_SYNC_ID = ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"39619\""
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Define configured sync ops"
          }), "\n\nrun_ht_sync_accounts = hightouch_sync_op.configured(\n    {", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync_id\""
          }), ": SFDC_ACCOUNTS_SYNC_ID}, name=", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"hightouch_sfdc_accounts\""
          }), "\n)\nrun_ht_sync_orgs = hightouch_sync_op.configured(\n    {", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync_id\""
          }), ": SFDC_ORGS_SYNC_ID}, name=", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"hightouch_sfdc_organizations\""
          }), "\n)\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Create a job with the defined resources, specifying the dependencies"
          }), "\n\n", _jsxs(_components.span, {
            className: "hljs-meta",
            children: ["@job(", _jsxs(_components.span, {
              className: "hljs-params",
              children: ["\n    resource_defs={\n        ", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"hightouch\""
              }), ": ht_resource,\n    }\n"]
            }), ")"]
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "def"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "ht_sfdc_job"
          }), "():\n    \n    ht_orgs = run_ht_sync_orgs(start_after=ht_contacts)\n    run_ht_sync_accounts(start_after=ht_orgs)\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Schedule it to run as often as you like with a cron expression"
          }), "\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# This example shows every 30 min"
          }), "\n\nevery_30_schedule = ScheduleDefinition(job=ht_sfdc_job, cron_schedule=", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*/30 * * * *\""
          }), ")\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To learn more, check out the ", _jsx(_components.a, {
          href: "https://hightouch.com/blog/introducing-the-hightouch-dagster-integration",
          children: "blog post"
        }), " about the extension."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
